"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowLeft, Send } from "lucide-react"
import { MobileBottomNav } from "../components/MobileBottomNav"
import { useAuth } from "../hooks/useAuth"
import Footer from "../components/Footer"
import { motion } from "framer-motion"
import { db } from "../firebase"
import { ref, push } from "../lib/firebase-database"

export default function ContactUsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.displayName || prev.name,
        email: user.email || prev.email,
      }))
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [user])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.phone || !formData.message) {
      setSubmitError("Name, phone number, and message are required fields")
      return
    }

    setIsSubmitting(true)
    setSubmitError("")

    try {
      const queriesRef = ref(db, "userQueries")
      await push(queriesRef, {
        ...formData,
        timestamp: Date.now(),
        userId: user?.uid || null,
      })

      setSubmitSuccess(true)
      setFormData({
        name: user?.displayName || "",
        email: user?.email || "",
        phone: "",
        subject: "",
        message: "",
      })

      setTimeout(() => {
        setSubmitSuccess(false)
      }, 5000)
    } catch (error) {
      console.error("Error submitting form:", error)
      setSubmitError("Failed to send message. Please try again later.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  }

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {isMobile ? (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold">Contact Us</h1>
          </div>
        </header>
      ) : (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold">VillaKosh</h1>
              </Link>
              <nav className="flex items-center space-x-4">
                <Link to="/" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Home
                </Link>
                <Link to="/contact" className="bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Contact
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      <motion.main
        className={`container mx-auto px-4 py-8 ${isMobile ? "pb-24" : ""}`}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="max-w-3xl mx-auto">
          <motion.div className="bg-white rounded-lg shadow-md overflow-hidden mb-8" variants={itemVariants}>
            <div className="h-48 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <h2 className="text-3xl font-bold text-white text-center">Get in Touch</h2>
            </div>

            <div className="p-6">
              <motion.p className="text-gray-600 mb-6 text-center" variants={itemVariants}>
                Notice something not working right?
                Hey! I'm the solo developer behind this website, so if you come across any bugs, glitches, or anything that seems off, please send me a screenshot through any of the platforms listed below.
                I’ll try to fix it as soon as I can.
                Thanks a lot for your patience and understanding — it really means a lot!
              </motion.p>

              {submitSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Your message has been sent successfully! We'll get back to you soon.</span>
                </div>
              )}

              {submitError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <motion.div className="mb-4" variants={itemVariants}>
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your name"
                  />
                </motion.div>

                <motion.div className="mb-4" variants={itemVariants}>
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your email address"
                  />
                </motion.div>

                <motion.div className="mb-4" variants={itemVariants}>
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your phone number"
                  />
                </motion.div>

                <motion.div className="mb-4" variants={itemVariants}>
                  <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Subject of your message"
                  />
                </motion.div>

                <motion.div className="mb-6" variants={itemVariants}>
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your message"
                  ></textarea>
                </motion.div>

                <motion.div className="flex justify-center" variants={itemVariants}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex items-center justify-center px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </motion.div>
              </form>
            </div>
          </motion.div>

          <motion.div className="grid md:grid-cols-3 gap-6 mb-8" variants={itemVariants}>
           {/*
            <a href="https://wa.me/918668737989" target="_blank" rel="noopener noreferrer">
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">WhatsApp</h3>
                <p className="text-gray-600">+91 8668737989</p>
                <p className="text-gray-600">Mon-Sun, 9am-6pm</p>
              </div>
            </a>  
            */}    
            
            <a href="https://www.instagram.com/villakosh.in" target="_blank" rel="noopener noreferrer">
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">

                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-pink-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 2 .3 2.5.5.6.2 1.1.5 1.6 1s.8 1 .9 1.6c.2.5.4 1.3.5 2.5.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 2-.5 2.5-.2.6-.5 1.1-1 1.6s-1 .8-1.6.9c-.5.2-1.3.4-2.5.5-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-2-.3-2.5-.5a4.2 4.2 0 01-2.6-2.6c-.2-.5-.4-1.3-.5-2.5C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-2 .5-2.5a4.2 4.2 0 012.6-2.6c.5-.2 1.3-.4 2.5-.5C8.4 2.2 8.8 2.2 12 2.2m0-2.2C8.7 0 8.3 0 7 .1 5.6.2 4.5.4 3.6.8a6.3 6.3 0 00-3 3C.2 4.5.1 5.6.1 7 .1 8.3 0 8.7 0 12s.1 3.7.1 5c.1 1.4.3 2.5.7 3.4a6.3 6.3 0 003 3c.9.4 2 .6 3.4.7 1.3.1 1.7.1 5 .1s3.7 0 5-.1c1.4-.1 2.5-.3 3.4-.7a6.3 6.3 0 003-3c.4-.9.6-2 .7-3.4.1-1.3.1-1.7.1-5s0-3.7-.1-5c-.1-1.4-.3-2.5-.7-3.4a6.3 6.3 0 00-3-3C19.5.4 18.4.2 17 .1 15.7 0 15.3 0 12 0z" />
                    <path d="M12 5.8a6.2 6.2 0 100 12.4A6.2 6.2 0 0012 5.8zm0 10.2a4 4 0 110-8.1 4 4 0 010 8.1zM18.4 4.6a1.4 1.4 0 11-2.8 0 1.4 1.4 0 012.8 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Instagram</h3>
                <p className="text-gray-600">villakosh.in</p>
                <p className="text-gray-600">Follow us for updates</p>
              </div>
            </a>

            <a href="https://www.youtube.com/@VillaKosh" target="_blank" rel="noopener noreferrer">
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">


                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.5 6.2c-.3-1.3-1.4-2.2-2.6-2.3C18.3 3.6 12 3.6 12 3.6s-6.3 0-8.9.3c-1.3.1-2.3 1-2.6 2.3C.1 8.9 0 11.2 0 11.2s0 2.3.5 5c.3 1.3 1.4 2.2 2.6 2.3 2.6.3 8.9.3 8.9.3s6.3 0 8.9-.3c1.3-.1 2.3-1 2.6-2.3.5-2.7.5-5 .5-5s0-2.3-.5-5zM9.7 14.7V7.7l6.3 3.5-6.3 3.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">YouTube</h3>
                <p className="text-gray-600">VillaKosh</p>
                <p className="text-gray-600">Subscribe for videos</p>
              </div>
            </a>

            <a href="mailto:contact@villakosh.in">
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Email</h3>
                <p className="text-gray-600">contact@villakosh.in</p>
                <p className="text-gray-600">24/7 Support</p>
              </div>
            </a>

            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Office</h3>
              <p className="text-gray-600"> We currently operate as a fully remote and do not maintain a physical office</p>
              <p className="text-gray-600">India</p>
            </div>
          </motion.div>

          <motion.div className="bg-white rounded-lg shadow-md overflow-hidden" variants={itemVariants}>
            <div className="h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.74109995709657!3d19.08219783958221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1651151717028!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="VillaKosh Office Location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </motion.main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav user={user} />}
    </div>
  )
}